import {Form, Formik, FormikProps} from 'formik';
import {Fragment, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import * as Yup from 'yup';

import {settingsApi} from 'api';
import {
    Button, Card, DataWrapper, ExternalIntegrationPurposeSelect, InputFormik, PasswordInputFormik,
    PredefinedNotifierType, ToggleFormik, UnsavedChangesLeavePrompt
} from 'components';
import {UserContext} from 'context';
import {AiProvider, ExternalIntegrationPurpose, ValidationCode} from 'enums';
import {useApiData} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {WatsonxSettings} from 'types';
import {apiUtils, dataUtils} from 'utils';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.SESSION.WATSONX;
const COMMON_T_KEYS = TRANSLATION_KEYS.COMMON;

type FormTemplateProps = {
    loading: boolean;
    integrationPurpose: ExternalIntegrationPurpose;
} & FormikProps<WatsonxSettings>;

const FIELDS = {
    ENABLED: 'enabled',
    SERVICE_URL: 'serviceUrl',
    VERSION: 'version',
    STT_MODEL: 'sttModel',
    ENVIRONMENT_ID: 'environmentId',
    API_KEY: 'authentication.apiKey',
};

const FormTemplate: React.FC<FormTemplateProps> = ({loading, values, initialValues, submitForm, isValid, integrationPurpose}) => {
    const {t} = useTranslation();
    const hasNoChanges = useMemo(() => dataUtils.isEqualValues(values, initialValues), [initialValues, values]);

    return (
        <Form className='tw-relative'>
            <DataWrapper isLoading={loading} hideContentOnLoading={false}>
                <Card>
                    <div className='tw-max-w-104'>
                        <ToggleFormik
                            name={FIELDS.ENABLED}
                            label={t(T_KEYS.LABELS.ENABLED)}
                            className='tw-my-4'
                        />
                        <InputFormik
                            name={FIELDS.SERVICE_URL}
                            label={t(T_KEYS.LABELS.SERVICE_URL)}
                            className='tw-w-full'
                            required={values.enabled}
                            spacing='top'
                        />
                        {integrationPurpose === ExternalIntegrationPurpose.ClientTextChat &&
                            <InputFormik
                                name={FIELDS.VERSION}
                                label={t(T_KEYS.LABELS.VERSION)}
                                className='tw-w-full'
                                required={values.enabled}
                                spacing='top'
                            />}
                        {integrationPurpose === ExternalIntegrationPurpose.STT &&
                            <InputFormik
                                name={FIELDS.STT_MODEL}
                                label={t(T_KEYS.LABELS.STT_MODEL)}
                                className='tw-w-full'
                                required={values.enabled}
                                spacing='top'
                            />}
                        {integrationPurpose === ExternalIntegrationPurpose.ClientTextChat &&
                            <PasswordInputFormik
                                name={FIELDS.ENVIRONMENT_ID}
                                label={t(T_KEYS.LABELS.ENVIRONMENT_ID)}
                                className='tw-w-full tw-mt-4'
                                spacing='bottom'
                                autoComplete='one-time-code'
                                required={values.enabled}
                            />}
                        <PasswordInputFormik
                            name={FIELDS.API_KEY}
                            label={t(T_KEYS.LABELS.API_KEY)}
                            className='tw-w-full tw-mt-4'
                            spacing='bottom'
                            required={values.enabled}
                            autoComplete='one-time-code'
                        />

                        <div className='tw-text-right tw-mt-controls'>
                            <Button
                                color='primary'
                                disabled={hasNoChanges || !isValid}
                                loading={loading}
                                onClick={submitForm}
                            >
                                {t(COMMON_T_KEYS.BUTTONS.SAVE)}
                            </Button>
                        </div>
                    </div>
                </Card>
            </DataWrapper>
            <UnsavedChangesLeavePrompt values={values} initialValues={initialValues} />
        </Form>
    );
};

// TODO: move out from sip settings
export const WatsonxSettingsPage: React.FC = () => {
    const {t} = useTranslation();
    const {settings: {providerPurposes}} = useContext(UserContext);
    const purposes = useMemo(() => providerPurposes[AiProvider.Watsonx] || [], [providerPurposes]);
    const [integrationPurpose, setIntegrationPurpose] = useState<ExternalIntegrationPurpose>(ExternalIntegrationPurpose.ClientTextChat);

    const [initialValues, setInitialValues] = useState<WatsonxSettings>({
        enabled: false,
        serviceUrl: '',
        version: '',
        sttModel: '',
        environmentId: '',
        authentication: {
            apiKey: ''
        }
    });

    const {
        send: fetchSettingsFn, loading: fetchLoading, data: settings
    } = useApiData(settingsApi.getWatsonx, {notificationType: PredefinedNotifierType.FetchError});

    const fetchSettings = useCallback(() => {
        fetchSettingsFn(integrationPurpose);
    }, [integrationPurpose, fetchSettingsFn]);

    const {send: updateSettings, loading: updateLoading} = useApiData(settingsApi.updateWatsonx,
        {onSuccess: fetchSettings, notificationType: PredefinedNotifierType.Save});

    const validationSchema = useMemo(() => Yup.object().shape({
        [FIELDS.ENABLED]: Yup.boolean(),
        [FIELDS.SERVICE_URL]: Yup.string()
            .when(FIELDS.ENABLED, {
                is: true,
                then: (schema) => schema.required(apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Required})),
            }),
        ...(integrationPurpose === ExternalIntegrationPurpose.ClientTextChat &&
            {
                [FIELDS.VERSION]: Yup.string()
                    .when(FIELDS.ENABLED, {
                        is: true,
                        then: (schema) => schema.required(apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Required})),
                    }),
                [FIELDS.ENVIRONMENT_ID]: Yup.string()
                    .when(FIELDS.ENABLED, {
                        is: true,
                        then: (schema) => schema.required(apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Required})),
                    }),
            }
        ),
        authentication: Yup.object().shape({
            apiKey: Yup.string()
                .when(FIELDS.ENABLED, {
                    is: true,
                    then: (schema) => schema.required(apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Required})),
                })
        })
    }), [t, integrationPurpose]);

    const onSubmit = useCallback((values: WatsonxSettings) => {
        updateSettings(integrationPurpose, values);
    }, [integrationPurpose, updateSettings]);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    useEffect(() => {
        setInitialValues({
            enabled: !!settings?.enabled,
            serviceUrl: settings?.serviceUrl || '',
            version: settings?.version || '',
            sttModel: settings?.sttModel || '',
            environmentId: settings?.environmentId || '',
            authentication: {
                apiKey: settings?.authentication?.apiKey || ''
            }
        });
    }, [settings]);

    return (
        <Fragment>
            <ExternalIntegrationPurposeSelect
                value={integrationPurpose}
                purposes={purposes}
                className='tw-mb-4 tw-max-w-104'
                onChange={setIntegrationPurpose}
            />
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                enableReinitialize
                onSubmit={onSubmit}
            >
                {(props) => (
                    <FormTemplate
                        {...props}
                        loading={fetchLoading || updateLoading}
                        integrationPurpose={integrationPurpose}
                    />
                )}
            </Formik>
        </Fragment>
    );
};